import { FeatureFlagIdReviewsPendingActions } from 'c1gen/features'
import { getOutcomeText } from 'components/core/task/outcome'
import { getOutcome } from 'components/core/task/outcome/getOutcomeText'
import { useFeatureChecker } from 'components/no-ui/capability'
import { ApprovalOneOfKind, ProvisionOneOfKind } from 'components/pages/policy/type'
import { taskTypeToString } from 'components/pages/task/type'
import { TaskValue } from 'lib/actions/task'
import { TaskTypeString } from 'lib/constants/task'
import { GetPendingActionsByTaskIDsRequest, ReviewPendingDecision } from 'pbts/c1/api/accessreview/v1/accessreview_pending'
import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { getPendingActionsByTaskIdsApi } from 'redux_toolkit/access_review/pending_actions'
import { PendingActionsStateV2, selectConsolidatedPendingActionState } from 'redux_toolkit/slices/pendingActionsSliceV2'

type TaskStatusFormattedMessageProps = {
    task: TaskValue;
}

export const TaskStatusFormattedMessage = ({ task }: TaskStatusFormattedMessageProps): React.ReactElement | string => {
    const currentStep = task.policy?.current
    const intl = useIntl()
    const taskTypeString = taskTypeToString(task.type)
    const hasPendingActionsFeature = useFeatureChecker(FeatureFlagIdReviewsPendingActions)
    const accessReviewId = task.accessReview?.id

    // Read consolidated pending action state (includes optimistic updates from PendingTicketActionTracker)
    const consolidatedPendingActionState = useSelector(
        (state: { pendingActionsV2: PendingActionsStateV2 }) => selectConsolidatedPendingActionState(state, task.id),
    )

    // Check for pending actions if task belongs to an access review and feature is enabled
    const { data: pendingActionsResponse } = getPendingActionsByTaskIdsApi.useGetPendingActionsByTaskIDs(
        GetPendingActionsByTaskIDsRequest.create({
            accessReviewId: accessReviewId || '',
            certifyTaskIds: [task.id],
        }),
        { actionable: !!accessReviewId && !!hasPendingActionsFeature, evictionAfterSeconds: 0 },
    )

    // Determine pending action from consolidated state (includes optimistic updates)
    // or fall back to API response for backwards compatibility
    const pendingAction = useMemo(() => {
        // Prefer consolidated state from pendingActionsSliceV2 (includes optimistic updates)
        if (consolidatedPendingActionState?.state.oneofKind === 'unlocked') {
            return consolidatedPendingActionState.state.unlocked
        }

        // Fall back to direct API response if consolidated state not available
        const apiPendingState = pendingActionsResponse?.states?.[task.id]
        if (apiPendingState?.state.oneofKind === 'unlocked') {
            return apiPendingState.state.unlocked
        }

        return undefined
    }, [consolidatedPendingActionState, pendingActionsResponse, task.id])

    const outcome = getOutcome(task)

    if (outcome) {
        return getOutcomeText(task, intl)
    }

    if (taskTypeString === TaskTypeString.Action && currentStep?.instance.oneofKind === 'action') {
        return <FormattedMessage id="components_task_status_action_in_progress" defaultMessage="Action in progress" />
    }

    // Check for wait step before checking for assignees (wait steps don't have assignees)
    if (currentStep?.instance.oneofKind === 'wait') {
        const waitInstance = currentStep.instance.wait
        if (waitInstance?.until?.oneofKind === 'condition') {
            return <FormattedMessage id="components_task_status_waiting_for_policy_condition" defaultMessage="Waiting for policy condition" />
        }
        if (waitInstance?.until?.oneofKind === 'untilTime') {
            return <FormattedMessage id="components_task_status_waiting_until_time" defaultMessage="Waiting until scheduled time" />
        }
        if (waitInstance?.until?.oneofKind === 'duration') {
            return <FormattedMessage id="components_task_status_waiting_for_duration" defaultMessage="Waiting for duration" />
        }
        // Fallback for any other wait type
        return <FormattedMessage id="components_task_status_waiting" defaultMessage="Waiting" />
    }

    const noOneIsAssignedToTask = task.stepApproverIds.length === 0

    if (noOneIsAssignedToTask) {
        return <FormattedMessage id="components_task_status_no_one_assigned" defaultMessage="Awaiting assignee" />
    }

    if (!currentStep) {
        return <FormattedMessage id="components_task_status_unknown" defaultMessage="Unknown task status" />
    }

    if (currentStep.instance.oneofKind === 'approval') {
        if (currentStep.instance.approval?.approval?.typ.oneofKind === ApprovalOneOfKind.WEBHOOK) {
            return <FormattedMessage id="components_task_status_awaiting_approval" defaultMessage="Waiting: webhook" />
        }

        if (taskTypeString === TaskTypeString.Certify) {
            // Check if there's a pending action and show appropriate message (only if feature is enabled)
            if (hasPendingActionsFeature && pendingAction) {
                const actionText = pendingAction.decision === ReviewPendingDecision.APPROVE
                    ? intl.formatMessage({ id: 'task_pending_action_certify', defaultMessage: 'certify' })
                    : intl.formatMessage({ id: 'task_pending_action_deny', defaultMessage: 'deny' })
                return (
                    <FormattedMessage
                        id="components_task_status_pending_action_awaiting_submission"
                        defaultMessage="Pending {action}: Awaiting submission"
                        values={{ action: actionText }}
                    />
                )
            }
            return <FormattedMessage id="components_task_status_awaiting_review" defaultMessage="Requires review" />
        }
        return <FormattedMessage id="components_task_status_awaiting_approval" defaultMessage="Requires approval" />
    }

    if (currentStep.instance.oneofKind === 'form') {
        return <FormattedMessage id="components_task_status_requires_additional_information" defaultMessage="Requires additional information" />
    }

    if (currentStep.instance.oneofKind === 'provision') {
        if (currentStep.instance.provision.provision?.provisionPolicy?.typ.oneofKind === ProvisionOneOfKind.WEBHOOK) {
            return <FormattedMessage id="components_task_status_awaiting_provision" defaultMessage="Executing webhook" />
        }

        if (currentStep.instance.provision.provision?.provisionPolicy?.typ.oneofKind === ProvisionOneOfKind.EXTERNAL_TICKET) {
            return <FormattedMessage id="components_task_status_awaiting_provision_external_ticket" defaultMessage="Provisioning with external ticket" />
        }

        if (taskTypeString === TaskTypeString.Revoke) {
            return <FormattedMessage id="components_task_status_awaiting_deprovision" defaultMessage="Awaiting deprovisioning" />
        }

        return <FormattedMessage id="components_task_status_awaiting_provision" defaultMessage="Awaiting provisioning" />
    }

    return <FormattedMessage id="components_task_status_awaiting_analysis" defaultMessage="Awaiting analysis" />
}
